/**
 * Accessible Language Switcher
 * Custom dropdown with keyboard navigation support
 */

class LangSwitcher {
  constructor(element) {
    this.element = element;
    this.trigger = element.querySelector('.lang-switcher__trigger');
    this.dropdown = element.querySelector('.lang-switcher__dropdown');
    this.options = Array.from(element.querySelectorAll('[role="option"]'));
    this.isOpen = false;
    this.currentIndex = this.options.findIndex(opt => opt.getAttribute('aria-selected') === 'true');

    this.init();
  }

  init() {
    // Click outside to close
    document.addEventListener('click', (e) => {
      if (!this.element.contains(e.target) && this.isOpen) {
        this.close();
      }
    });

    // Trigger button events
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });

    this.trigger.addEventListener('keydown', (e) => {
      this.handleTriggerKeydown(e);
    });

    // Option events
    this.options.forEach((option, index) => {
      const link = option.querySelector('a');

      link.addEventListener('click', (e) => {
        this.selectOption(index);
      });

      link.addEventListener('keydown', (e) => {
        this.handleOptionKeydown(e, index);
      });
    });
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.dropdown.classList.add('is-open');

    // Focus first or selected option
    const focusIndex = this.currentIndex >= 0 ? this.currentIndex : 0;
    this.focusOption(focusIndex);
  }

  close() {
    this.isOpen = false;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.dropdown.classList.remove('is-open');
    this.trigger.focus();
  }

  selectOption(index) {
    // Update aria-selected
    this.options.forEach((opt, i) => {
      opt.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });

    this.currentIndex = index;

    // Update trigger text with short code
    const link = this.options[index].querySelector('a');
    const lang = link.getAttribute('lang');
    const shortCode = { fr: 'Fr', en: 'En', zh: 'Zh' };
    this.trigger.querySelector('.lang-switcher__selected').textContent = shortCode[lang] || lang;
  }

  focusOption(index) {
    if (index >= 0 && index < this.options.length) {
      this.options[index].querySelector('a').focus();
    }
  }

  handleTriggerKeydown(e) {
    switch(e.key) {
      case 'Enter':
      case ' ':
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        this.open();
        break;
      case 'Escape':
        if (this.isOpen) {
          e.preventDefault();
          this.close();
        }
        break;
    }
  }

  handleOptionKeydown(e, currentIndex) {
    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = currentIndex < this.options.length - 1 ? currentIndex + 1 : 0;
        this.focusOption(nextIndex);
        break;

      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : this.options.length - 1;
        this.focusOption(prevIndex);
        break;

      case 'Home':
        e.preventDefault();
        this.focusOption(0);
        break;

      case 'End':
        e.preventDefault();
        this.focusOption(this.options.length - 1);
        break;

      case 'Escape':
        e.preventDefault();
        this.close();
        break;

      case 'Tab':
        this.close();
        break;
    }
  }
}

// Initialize all language switchers
document.addEventListener('DOMContentLoaded', () => {
  const switchers = document.querySelectorAll('.lang-switcher');
  switchers.forEach(switcher => new LangSwitcher(switcher));
});
